import { inject, Injectable, signal, computed } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  selectActiveSubmissionId,
  selectHasNotificationsDetails,
  selectHasOpenDocumentThreads,
  selectLoadingSubmission,
  selectLoadingSubmissionList,
  selectLoadingWorkspaceTypes,
  selectOpenDocumentThreadsCount,
  selectSubmission,
  selectSubmissionList,
  selectWorkspaceTypes,
} from '../redux/submission.selectors';
import { HttpClient, HttpContext } from '@angular/common/http';
import environment from '../../../../environments/environment';
import {
  assignUserSuccess,
  loadingSubmission,
  loadingSubmissionTeam,
  storeActiveSubmissionId,
  storeSubmission,
  storeSubmissionTeam,
  updateSubmissionStatus,
} from '../redux/submission.actions';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  forkJoin,
  mergeMap,
  Observable,
  of,
  take,
} from 'rxjs';
import { finalize, map, switchMap, tap, catchError } from 'rxjs/operators';
import {
  irbPriorityTypes,
  SectionAssignmentContact,
  Submission,
  SubmissionQcStatus,
  SubmissionTodoCounts,
} from '../models/submission.model';
import {
  clearQuestionnaire,
  storeActiveQuestionnaireId,
} from '../redux/questionnaire.actions';

import { storeStudyIdOutcome } from '../redux/outcome.actions';

import {
  setEntityRole,
  setRolePermissions,
} from '@shared/store/actions/role.actions';
import { UtilsService } from '@shared/services/utils.service';
import {
  selectLoadingSubmissionTeam,
  selectSubmissionTeam,
} from '../redux/questionnaire.selectors';
import { CUSTOM_ERROR_HANDLING } from '@shared/http';

@Injectable()
export class SubmissionsService {
  private readonly _utils = inject(UtilsService);
  private readonly _store = inject(Store);
  private readonly _http = inject(HttpClient);

  private _activeSubmissionId$: Observable<string> = this._store
    .select(selectActiveSubmissionId)
    .pipe(
      filter((id): id is string => !!id),
      distinctUntilChanged(),
    );

  private _submissionSubject = new BehaviorSubject<Submission | null>(null);
  private _qcStatusSubject = new BehaviorSubject<SubmissionQcStatus | null>(
    null,
  );
  private _todoCountsSubject = new BehaviorSubject<SubmissionTodoCounts | null>(
    null,
  );
  private readonly _$stageLoading = signal<boolean>(false);

  /**
   * Loading state for the stage update
   */
  public readonly $stageLoading = computed(() => this._$stageLoading());

  getSubmission$ = this._submissionSubject
    .asObservable()
    .pipe(
      filter(
        (submission): submission is Submission =>
          submission !== null && Object.keys(submission).length > 0,
      ),
    ) as Observable<Submission>;

  qcStatus$ = this._qcStatusSubject
    .asObservable()
    .pipe(
      filter(
        (qcStatus): qcStatus is SubmissionQcStatus =>
          qcStatus !== null && Object.keys(qcStatus).length > 0,
      ),
    ) as Observable<SubmissionQcStatus>;

  todoCounts$ = this._todoCountsSubject
    .asObservable()
    .pipe(
      filter(
        (todoCounts): todoCounts is SubmissionTodoCounts =>
          todoCounts !== null && Object.keys(todoCounts).length > 0,
      ),
    ) as Observable<SubmissionTodoCounts>;

  submission$: Observable<Submission> = this._store
    .select(selectSubmission)
    .pipe(
      filter((submission): submission is Submission => !!submission),
      distinctUntilChanged(),
    );

  loadingSubmission$ = this._store.select(selectLoadingSubmission);

  submissionList$ = this._store.select(selectSubmissionList);
  loadingSubmissionList$ = this._store.select(selectLoadingSubmissionList);

  workspaceTypes$ = this._store.select(selectWorkspaceTypes);
  loadingWorkspaceTypes$ = this._store.select(selectLoadingWorkspaceTypes);
  hasNotificationsDetails$ = this._store.select(selectHasNotificationsDetails);
  hasNotificationsDocuments$ = this._store.select(selectHasOpenDocumentThreads);
  notificationsDocumentsCount$ = this._store.select(
    selectOpenDocumentThreadsCount,
  );

  entityType$ = this.submission$.pipe(
    map((submission) => submission.entity_type),
    distinctUntilChanged(),
  );

  /**
   * Get a single submission
   */
  getSubmission(id: string): Observable<Submission> {
    return combineLatest([
      this._store.select(selectLoadingSubmission),
      this._store.select(selectSubmission),
    ]).pipe(
      take(1),
      mergeMap(([loading, submission]): Observable<Submission> => {
        if (submission?.int_id !== parseInt(id) || loading?.id !== id) {
          this.loadSubmission(id);
        } else {
          this._store.dispatch(storeActiveSubmissionId({ id: submission.id }));
        }
        return this.submission$;
      }),
    );
  }

  loadSubmission(id: string): void {
    this._store.dispatch(loadingSubmission({ id }));
    this._loadSubmission(id).subscribe();
  }

  _loadSubmission(id: string): Observable<void> {
    return this._fetchSubmission(id).pipe(
      switchMap((submission) => {
        if (submission) {
          this._submissionSubject.next(submission);
          this._store.dispatch(storeActiveSubmissionId({ id: submission.id }));
          this._store.dispatch(
            setEntityRole({
              entityId: submission.id,
              role: submission.role,
            }),
          );

          if (submission.role) {
            const roleId = submission.role.id;
            const permissions = this._utils.convertKeysToCamelCase(
              submission.role.permissions,
            );
            this._store.dispatch(setRolePermissions({ roleId, permissions }));
          }

          this._store.dispatch(clearQuestionnaire());
        }

        if (submission.version === 2) {
          /**
           *@NOTE: For V2 submissions, skip fetching QC status and ToDo counts here.
           * These will be handled in the V2 submission service.
           */
          this._store.dispatch(
            storeSubmission({ submission: { ...submission } }),
          );

          return of();
        }

        const studyId = submission?.study?.id ?? submission?.entity_id ?? '';
        this._store.dispatch(storeStudyIdOutcome({ studyId }));

        const questionnaireId = String(
          submission?.data?.questionnaire_id || '',
        );
        this._store.dispatch(
          storeActiveQuestionnaireId({ id: questionnaireId }),
        );

        // Wait for QC status + ToDo counts
        return forkJoin([
          this._fetchQcStatus(submission.id),
          this._fetchTodoCounts(submission.id),
        ]).pipe(
          tap(([qcStatus, todoCounts]) => {
            // Update subjects
            this._qcStatusSubject.next(qcStatus);
            this._todoCountsSubject.next(todoCounts);

            // Update store
            this._store.dispatch(
              storeSubmission({
                submission: {
                  ...submission,
                  qc_status: qcStatus,
                },
              }),
            );

            const hasNotificationsDetails =
              (todoCounts?.data?.questionnaire ?? 0) > 0;
            const hasNotificationsDocuments =
              (qcStatus?.data?.documents ?? 0) > 0 ||
              (todoCounts?.data?.document ?? 0) > 0;

            this._store.dispatch(
              updateSubmissionStatus({
                qcStatus,
                todoCounts,
                hasNotificationsDetails,
                hasNotificationsDocuments,
              }),
            );
          }),
          map(() => void 0), // ensure type Observable<void>
        );
      }),
    );
  }

  loadSubmissionTeam(id: string) {
    return combineLatest([
      this._store.select(selectLoadingSubmissionTeam(id)),
      this._store.select(selectSubmissionTeam(id)),
    ]).pipe(
      take(1),
      mergeMap(([loading, team]) => {
        if (!loading && !team) {
          this._store.dispatch(loadingSubmissionTeam({ id }));
          this._fetchSubmissionTeam(id).subscribe((team) => {
            this._store.dispatch(storeSubmissionTeam({ id, team }));
          });
        }

        return this._store
          .select(selectSubmissionTeam(id))
          .pipe(filter((team) => !!team));
      }),
    );
  }

  assignSection(id: string, sectionId: string, userId?: string | null) {
    return this._http
      .post(`${environment.irisApi}/workspace/${id}/section/${sectionId}/`, {
        user_id: userId ? userId : null,
      })
      .pipe(
        tap(() =>
          this._store.dispatch(
            assignUserSuccess({ workspaceId: id, sectionId, userId }),
          ),
        ),
      );
  }

  assignSectionV2(id: string, sectionId: string, userId?: string | null) {
    return this._http
      .post(`${environment.irisApi}/v2/workspace/${id}/section/${sectionId}/`, {
        user_id: userId ? userId : null,
      })
      .pipe(
        tap(() =>
          this._store.dispatch(
            assignUserSuccess({ workspaceId: id, sectionId, userId }),
          ),
        ),
      );
  }

  updateSubmission(submissionId: string, payload: any) {
    this._store.dispatch(loadingSubmission({ id: submissionId }));

    return this._http
      .put<any>(`${environment.irisApi}/workspace/${submissionId}/`, payload)
      .subscribe((response) => {
        this._store.dispatch(storeSubmission({ submission: response }));
      });
  }

  updateSubmissionStatus() {
    return of(
      this._activeSubmissionId$.subscribe((submissionId) => {
        return combineLatest([
          this._fetchQcStatus(submissionId),
          this._fetchTodoCounts(submissionId),
        ])
          .pipe(
            map(
              ([qcStatus, todoCounts]: [
                SubmissionQcStatus,
                SubmissionTodoCounts,
              ]) => {
                const hasNotificationsDetails =
                  todoCounts.data.questionnaire > 0;
                const hasNotificationsDocuments =
                  qcStatus.data.documents > 0 || todoCounts.data.document > 0;

                this._store.dispatch(
                  updateSubmissionStatus({
                    qcStatus,
                    todoCounts,
                    hasNotificationsDetails,
                    hasNotificationsDocuments,
                  }),
                );

                this.submission$.pipe(take(1)).subscribe((submission) => {
                  this._store.dispatch(
                    storeSubmission({
                      submission: {
                        ...submission,
                        qc_status: qcStatus,
                        todo_counts: todoCounts,
                        hasNotificationsDetails,
                        hasNotificationsDocuments,
                      },
                    }),
                  );
                });
              },
            ),
          )
          .subscribe();
      }),
    );
  }

  updateWorkspaceStage(id: string): Observable<void> {
    this._$stageLoading.set(true);
    return this._http
      .put(`${environment.irisApi}/workspace/${id}/stage/`, {})
      .pipe(
        switchMap(() => this._loadSubmission(id)), // waits until done
        finalize(() => this._$stageLoading.set(false)),
      );
  }

  downloadDraftQuestionnaire(workspaceId: string) {
    return this._http.get<Blob>(
      `${environment.irisApi}/questionnaire/workspace/${workspaceId}/answer/pdf/draft/`,
      {
        observe: 'response',
        responseType: 'blob' as 'json',
      },
    );
  }

  checkIfEmailExists(email: string) {
    const encodedEmail = encodeURIComponent(email);
    return this._http.get<boolean>(
      `${environment.irisApi}/auth/user/exists/?email=${encodedEmail}`,
    );
  }

  updateIRBStatus(workspaceId: string, status: irbPriorityTypes) {
    return this._http.put(
      `${environment.irisApi}/workspace/priority/${workspaceId}/`,
      status,
    );
  }

  acknowledgeFailedDocuments(workspaceId: string, documentIds: string[]) {
    const _url = `${environment.irisApi}/workspace/${workspaceId}/acknowledge-failed-documents/`;
    return this._http.put(_url, {
      document_ids: documentIds,
    });
  }

  fetchSubmissionTeam(id: string) {
    return this._http
      .get<{
        data: SectionAssignmentContact[];
      }>(`${environment.irisApi}/workspace/${id}/section-assignment/contact/`)
      .pipe(map((response) => response.data));
  }

  private _fetchSubmissionTeam(id: string) {
    return this._http
      .get<{
        data: SectionAssignmentContact[];
      }>(`${environment.irisApi}/workspace/${id}/section-assignment/contact/`)
      .pipe(map((response) => response.data));
  }

  private _fetchSubmission(id: string) {
    return this._http.get<Submission>(
      `${environment.irisApi}/workspace/${id}/`,
    );
  }

  private _fetchQcStatus(id: string) {
    return this._http.get<SubmissionQcStatus>(
      `${environment.irisApi}/workspace/${id}/qc_status/`,
    );
  }

  private _fetchTodoCounts(id: string) {
    const context = new HttpContext().set(CUSTOM_ERROR_HANDLING, 'silent-mode');
    return this._http
      .get<SubmissionTodoCounts>(
        `${environment.irisApi}/message/${id}/todo-count/`,
        { context },
      )
      .pipe(
        catchError(() =>
          of({
            data: { questionnaire: 0, document: 0 },
          } as SubmissionTodoCounts),
        ),
      );
  }
}
